/* ───────────────────────────────────────────────────────────────────────────
 * ogpUrlContext.js — single source of truth for OgPumper URL bootstrap.
 *
 * Mirrors the auth/context pattern used by ForecastDashboard.js + Charts.js:
 * the demo/admin webroot loads the React app at e.g. demo.ogpumper.net with
 * tenant/auth/lease params on the URL, and the React components read them
 * directly via window.location.search. No PHP proxy, no env-bundled secrets.
 *
 * Expected URL shape (any subset of these is fine — components fall back to
 * sensible defaults / env-supplied values for local dev):
 *
 *   ?Company=lcs
 *   &UserID=admin
 *   &Name=LCS+Production
 *   &App=ogPumper
 *   &token=<JWT>
 *   &grant=<JWT>
 *   &LeaseID=CARTERBF        (or `lease=` — both accepted)
 *   &horizon=60
 *   &streams=Oil,Gas,Water
 *   &include_llm=1
 *
 * For local dev only: REACT_APP_LCS_TOKEN / REACT_APP_LCS_GRANT in
 * .env.development.local (gitignored) act as fallbacks if the URL omits them.
 * ──────────────────────────────────────────────────────────────────────── */

const _safeQS = () => {
  if (typeof window === "undefined") return new URLSearchParams();
  try { return new URLSearchParams(window.location.search); }
  catch { return new URLSearchParams(); }
};

function _envFallback(key) {
  if (typeof process === "undefined" || !process?.env) return "";
  return (process.env[key] || "").trim();
}

/**
 * Read the OgPumper deployment context from the current URL. Same param
 * names as ForecastDashboard / Charts.js so the React components share one
 * convention. Returns the full bag — callers pick what they need.
 */
export function getOgPumperUrlContext() {
  const qs = _safeQS();

  // Auth — URL first; env fallback ONLY for local dev (unset in prod build).
  const token = (qs.get("token") || "").trim() || _envFallback("REACT_APP_LCS_TOKEN");
  const grant = (qs.get("grant") || "").trim() || _envFallback("REACT_APP_LCS_GRANT");

  // Tenant context
  const company = (qs.get("Company") || qs.get("company") || "").trim();
  const userId = (qs.get("UserID") || qs.get("user") || qs.get("userid") || "").trim();
  const name = (qs.get("Name") || qs.get("name") || "").trim();
  const app = (qs.get("App") || qs.get("app") || "").trim();

  // Forecast / overlay defaults — let the URL override but keep sane defaults.
  const leaseFromUrl = (qs.get("LeaseID") || qs.get("lease") || qs.get("leaseid") || "").trim();
  const horizon = parseInt(qs.get("horizon") || "60", 10) || 60;
  const streams = (qs.get("streams") || "Oil,Gas,Water").trim();
  const includeLlm = qs.get("include_llm") === "0" ? "0" : "1";

  return {
    token,
    grant,
    company: company || "lcs",
    userId: userId || "admin",
    name: name || "LCS Production",
    app: app || "ogPumper",
    leaseFromUrl,
    horizon,
    streams,
    includeLlm,
    hasAuth: Boolean(token && grant),
  };
}

/**
 * Append every OgPumper context param to a URL's query string, OVERWRITING
 * any client-supplied values. Used by lcsClient when building overlay_staged
 * requests so the upstream gets the same auth bag the parent page received.
 */
export function applyContextToUrl(url, ctx, extras = {}) {
  if (ctx.token) url.searchParams.set("token", ctx.token);
  if (ctx.grant) url.searchParams.set("grant", ctx.grant);
  if (ctx.company) url.searchParams.set("Company", ctx.company);
  if (ctx.userId) url.searchParams.set("UserID", ctx.userId);
  if (ctx.name) url.searchParams.set("Name", ctx.name);
  if (ctx.app) url.searchParams.set("App", ctx.app);
  if (extras.includeLlm ?? ctx.includeLlm) {
    url.searchParams.set("include_llm", extras.includeLlm ?? ctx.includeLlm);
  }
  return url;
}

export default getOgPumperUrlContext;
